/**
 * Q.AI Design Tokens - Unified Brand System
 *
 * PURPOSE: Single source of truth for Q.AI branding and styling
 * Based on Tech Implementation Guide specifications
 *
 * @version 1.0.0
 * @date December 2025
 *
 * USAGE:
 * import { QAI_COLORS, QAI_TYPOGRAPHY } from '../theme/qaiDesignTokens';
 *
 * MIGRATION:
 * - Use QAI_COLORS.brand.primary for cyan accent (#00D4FF)
 * - Use QAI_COLORS.semantic for buy/sell/hold colors
 * - Section headers should use QAI_COLORS.brand.primary (NOT red)
 */

// ═══════════════════════════════════════════════════════════════════════════
// BRAND COLORS - PHASE 1.3: Color System Enforcement
// Doc Reference: QAI_DASHBOARD.2.md TASK 1.3
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_COLORS = {
  // Primary Brand - Cyan accent (STRICT per spec)
  brand: {
    primary: '#00d4ff',       // Main brand accent - cyan
    primaryDim: '#00a8cc',    // Dimmed cyan
    primaryHover: '#00B8E6',  // Hover state
    primaryMuted: 'rgba(0, 212, 255, 0.15)', // Backgrounds
    primaryBorder: 'rgba(0, 212, 255, 0.30)', // Borders
    gradient: 'linear-gradient(135deg, #00D4FF 0%, #00A3CC 100%)',
  },

  // Semantic Colors (Trading signals - STRICT per spec)
  semantic: {
    // Success/Buy/Bullish - STRICT: #00c853
    bullish: '#00c853',       // STRICT per spec
    bullishHover: '#00b34a',
    bullishMuted: 'rgba(0, 200, 83, 0.12)',
    bullishBorder: 'rgba(0, 200, 83, 0.25)',
    // Aliases for backward compatibility
    success: '#00c853',
    successHover: '#00b34a',
    successMuted: 'rgba(0, 200, 83, 0.12)',
    successBorder: 'rgba(0, 200, 83, 0.25)',

    // Danger/Sell/Bearish - STRICT: #ff1744
    bearish: '#ff1744',       // STRICT per spec
    bearishHover: '#e6153d',
    bearishMuted: 'rgba(255, 23, 68, 0.12)',
    bearishBorder: 'rgba(255, 23, 68, 0.25)',
    // Aliases for backward compatibility
    danger: '#ff1744',
    dangerHover: '#e6153d',
    dangerMuted: 'rgba(255, 23, 68, 0.12)',
    dangerBorder: 'rgba(255, 23, 68, 0.25)',

    // Warning/Hold/Caution - STRICT: #ffc400
    neutral: '#ffc400',       // STRICT per spec
    neutralHover: '#e6b000',
    neutralMuted: 'rgba(255, 196, 0, 0.12)',
    neutralBorder: 'rgba(255, 196, 0, 0.25)',
    // Aliases for backward compatibility
    warning: '#ffc400',
    warningHover: '#e6b000',
    warningMuted: 'rgba(255, 196, 0, 0.12)',
    warningBorder: 'rgba(255, 196, 0, 0.25)',

    // Info - STRICT: #2196f3
    info: '#2196f3',
    infoMuted: 'rgba(33, 150, 243, 0.12)',
    infoBorder: 'rgba(33, 150, 243, 0.25)',
  },

  // Background Hierarchy (Dark theme - STRICT per spec)
  background: {
    primary: '#0a0a0f',       // Main app background (STRICT)
    surface: '#12121a',       // Card backgrounds (STRICT)
    surface2: '#1a1a24',      // Secondary surface (STRICT)
    surface3: '#22222e',      // Tertiary surface (STRICT)
    surfaceHover: '#22222e',  // Hover state
    elevated: '#252542',      // Modals, dropdowns
    input: '#12121a',         // Input fields
  },

  // Text Hierarchy (WCAG AA compliant - STRICT per spec)
  text: {
    primary: '#f0f0f5',       // Headlines, important text (STRICT)
    secondary: '#9ca3af',     // Body text (STRICT)
    muted: '#6b7280',         // Labels, captions (STRICT)
    disabled: '#4b5563',      // Disabled text (STRICT)
    dim: '#71717a',           // Placeholders, hints
    inverse: '#0a0a0f',       // Text on light backgrounds
  },

  // Borders (STRICT per spec)
  border: {
    default: '#2a2a3a',       // STRICT per spec
    subtle: '#1f1f2e',        // STRICT per spec
    hover: 'rgba(255, 255, 255, 0.15)',
    active: 'rgba(255, 255, 255, 0.20)',
    accent: 'rgba(0, 212, 255, 0.40)',
  },

  // Legacy mapping (for backward compatibility with signalBoardTheme)
  legacy: {
    signalBuy: '#27B56D',     // Keep existing green for consistency
    signalSell: '#EF4444',    // Keep existing red
    signalNeutral: '#A1A1AA',
    signalWarning: '#F59E0B',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TYPOGRAPHY - PHASE 1.1: Institutional-Grade Type Scale System
// Doc Reference: QAI_DASHBOARD.2.md TASK 1.1
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_FONTS = {
  primary: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', 'Fira Code', 'Roboto Mono', monospace",
};

/**
 * Type Scale System (STRICT - per expert spec)
 * --text-2xs: 10px (micro labels)
 * --text-xs: 11px (table headers)
 * --text-sm: 13px (table data, body)
 * --text-base: 14px (section titles)
 * --text-lg: 16px (emphasis)
 * --text-xl: 20px (sub-headers)
 * --text-2xl: 28px (hero price)
 */
export const QAI_TYPE_SCALE = {
  '2xs': '10px',   // micro labels, minimum readable
  'xs': '11px',    // table headers, uppercase labels
  'sm': '13px',    // table data, body text
  'base': '14px',  // section titles, standard text
  'lg': '16px',    // emphasis, score displays
  'xl': '20px',    // sub-headers
  '2xl': '28px',   // hero price display
};

/**
 * Letter Spacing Scale (STRICT)
 */
export const QAI_TRACKING = {
  tight: '-0.01em',    // numbers, prices
  normal: '0',         // body text
  wide: '0.05em',      // uppercase headers
  wider: '0.1em',      // small uppercase labels
};

export const QAI_TYPOGRAPHY = {
  // Headers
  h1: {
    fontSize: QAI_TYPE_SCALE['2xl'],
    fontWeight: 700,
    lineHeight: 1.2,
    fontFamily: QAI_FONTS.primary,
  },
  h2: {
    fontSize: QAI_TYPE_SCALE.xl,
    fontWeight: 600,
    lineHeight: 1.3,
    fontFamily: QAI_FONTS.primary,
  },
  h3: {
    fontSize: QAI_TYPE_SCALE.lg,
    fontWeight: 600,
    lineHeight: 1.3,
    fontFamily: QAI_FONTS.primary,
  },

  // Body - UPDATED per spec
  body: {
    fontSize: QAI_TYPE_SCALE.base,  // 14px
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: QAI_FONTS.primary,
  },
  bodySmall: {
    fontSize: QAI_TYPE_SCALE.sm,  // 13px - table data
    fontWeight: 400,
    lineHeight: 1.4,
    fontFamily: QAI_FONTS.primary,
  },

  // Labels - UPDATED per spec
  label: {
    fontSize: QAI_TYPE_SCALE.sm,  // 13px
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: QAI_TRACKING.normal,
    fontFamily: QAI_FONTS.primary,
  },
  labelUppercase: {
    fontSize: QAI_TYPE_SCALE.xs,  // 11px - table headers
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: QAI_TRACKING.wide,  // 0.05em
    textTransform: 'uppercase',
    fontFamily: QAI_FONTS.primary,
  },
  labelMicro: {
    fontSize: QAI_TYPE_SCALE['2xs'],  // 10px - micro labels
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: QAI_TRACKING.wider,  // 0.1em
    textTransform: 'uppercase',
    fontFamily: QAI_FONTS.primary,
  },

  // Numbers (monospace) - UPDATED per spec
  price: {
    fontSize: QAI_TYPE_SCALE.sm,  // 13px
    fontWeight: 600,
    fontFamily: QAI_FONTS.mono,
    letterSpacing: QAI_TRACKING.tight,
  },
  priceHero: {
    fontSize: QAI_TYPE_SCALE['2xl'],  // 28px - hero price display
    fontWeight: 700,
    fontFamily: QAI_FONTS.mono,
    letterSpacing: '-0.02em',
  },
  score: {
    fontSize: QAI_TYPE_SCALE.lg,  // 16px
    fontWeight: 700,
    fontFamily: QAI_FONTS.mono,
  },
  bigNumber: {
    fontSize: QAI_TYPE_SCALE['2xl'],  // 28px
    fontWeight: 700,
    fontFamily: QAI_FONTS.mono,
    letterSpacing: '-0.02em',
  },

  // Table specific - NEW per spec
  tableHeader: {
    fontSize: QAI_TYPE_SCALE.xs,  // 11px UPPERCASE
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: QAI_TRACKING.wide,  // 0.05em
    textTransform: 'uppercase',
    fontFamily: QAI_FONTS.primary,
  },
  tableData: {
    fontSize: QAI_TYPE_SCALE.sm,  // 13px
    fontWeight: 400,
    fontFamily: QAI_FONTS.primary,
  },
  tableDataMono: {
    fontSize: QAI_TYPE_SCALE.sm,  // 13px
    fontWeight: 500,
    fontFamily: QAI_FONTS.mono,
    letterSpacing: QAI_TRACKING.tight,
  },

  // Section Headers (for DNA panels) - UPDATED per spec
  sectionHeader: {
    fontSize: QAI_TYPE_SCALE.xs,  // 11px UPPERCASE
    fontWeight: 600,
    letterSpacing: QAI_TRACKING.wide,  // 0.05em
    textTransform: 'uppercase',
    fontFamily: QAI_FONTS.primary,
  },
  sectionTitle: {
    fontSize: QAI_TYPE_SCALE.base,  // 14px - for larger section titles
    fontWeight: 600,
    letterSpacing: QAI_TRACKING.wide,
    textTransform: 'uppercase',
    fontFamily: QAI_FONTS.primary,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// SPACING - PHASE 1.2: Data-Dense Spacing Token System
// Doc Reference: QAI_DASHBOARD.2.md TASK 1.2
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Spacing Scale (STRICT - per expert spec)
 * --space-0: 0
 * --space-1: 4px
 * --space-2: 6px
 * --space-3: 8px (PRIMARY gap)
 * --space-4: 12px (card padding)
 * --space-5: 16px (section gap)
 * --space-6: 24px (major sections)
 */
export const QAI_SPACING = {
  0: '0',
  1: '4px',     // micro spacing
  2: '6px',     // tight spacing
  3: '8px',     // PRIMARY gap - most common
  4: '12px',    // card padding, comfortable
  5: '16px',    // section gap
  6: '24px',    // major sections
  // Legacy aliases for backward compatibility
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '20px',
  xl: '24px',
  xxl: '32px',
};

/**
 * Component Heights (STRICT - per expert spec)
 */
export const QAI_HEIGHTS = {
  rowCompact: '32px',      // Compact table rows
  rowNormal: '36px',       // Standard table rows / section headers
  rowData: '40px',         // Data rows with more content
  header: '48px',          // Top nav header
  sectionTitle: '36px',    // Section title bars
  inputField: '40px',      // Form inputs
  button: '32px',          // Standard buttons
  buttonSmall: '28px',     // Compact buttons
};

/**
 * Layout Widths (STRICT - per expert spec)
 */
export const QAI_WIDTHS = {
  sidebar: '180px',        // Left watchlist panel
  chat: '280px',           // Right chat panel
  center: '1fr',           // Center panel (flexible)
  columnGap: '8px',        // Gap between columns
};

// ═══════════════════════════════════════════════════════════════════════════
// BORDER RADIUS
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_RADIUS = {
  xs: '4px',   // Badges, small elements
  sm: '6px',   // Buttons, inputs
  md: '8px',   // Small cards
  lg: '12px',  // Cards, panels
  xl: '16px',  // Large containers
  full: '9999px', // Pills, avatars
};

// ═══════════════════════════════════════════════════════════════════════════
// LAYOUT - PHASE 1.2 (continued): UI Density Enhancement
// Doc Reference: QAI_DASHBOARD.2.md TASK 1.2
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_LAYOUT = {
  // Column widths (STRICT per spec)
  colWatchlist: '180px',     // Left sidebar
  colChat: '280px',          // Right chat panel (reduced from 320px)
  colCenter: '1fr',          // Center flexible

  // Row heights (compact mode - STRICT)
  rowCompact: '32px',        // Compact table rows
  rowNormal: '36px',         // Standard rows / section headers
  rowExpanded: '48px',       // Legacy expanded rows

  // Gaps (data-dense spacing - STRICT)
  gapSection: '8px',         // Between sections
  gapCard: '6px',            // Between cards
  gapInline: '4px',          // Inline elements
  gapColumn: '8px',          // Between columns in 2-col grid

  // Padding (compact - STRICT)
  paddingCard: '8px 12px',   // Card internal padding
  paddingCompact: '4px 8px', // Tight padding
  paddingCell: '4px 8px',    // Table cell padding - PHASE 3.1: 8px horizontal per spec

  // Component heights (STRICT per spec)
  headerCompact: '40px',     // Chat header
  headerNav: '48px',         // Top navigation
  dnaRowHeight: '48px',      // DNA confluence row
  tradeSetupHeight: '56px',  // Trade setup row
  sectionHeader: '36px',     // Section title bar
};

// ═══════════════════════════════════════════════════════════════════════════
// SHADOWS
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_SHADOWS = {
  card: '0 4px 6px rgba(0, 0, 0, 0.3)',
  cardHover: '0 8px 16px rgba(0, 0, 0, 0.4)',
  dropdown: '0 8px 24px rgba(0, 0, 0, 0.5)',
  glow: {
    cyan: '0 0 20px rgba(0, 212, 255, 0.3)',
    green: '0 0 20px rgba(0, 255, 136, 0.3)',
    red: '0 0 20px rgba(255, 71, 87, 0.3)',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TRANSITIONS
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_TRANSITIONS = {
  fast: '150ms ease',
  normal: '200ms ease-out',
  slow: '300ms ease',
  expand: '200ms ease-out',
};

// ═══════════════════════════════════════════════════════════════════════════
// DNA PANEL ICONS (for section headers)
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_ICONS = {
  ema: '📈',
  fibonacci: '📐',
  momentum: '⚡',
  pivot: '🎯',
  rsi: '📊',
  structure: '🏗️',
  atr: '📉',
  confluence: '🧬',
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get bias color based on direction
 * @param {'LONG' | 'SHORT' | 'NEUTRAL' | 'BULLISH' | 'BEARISH'} bias
 * @returns {string} Color hex
 */
export const getBiasColor = (bias) => {
  const upperBias = String(bias).toUpperCase();
  if (upperBias === 'LONG' || upperBias === 'BULLISH' || upperBias === 'BUY') {
    return QAI_COLORS.semantic.success;
  }
  if (upperBias === 'SHORT' || upperBias === 'BEARISH' || upperBias === 'SELL') {
    return QAI_COLORS.semantic.danger;
  }
  return QAI_COLORS.text.muted;
};

/**
 * Get bias background color
 * @param {'LONG' | 'SHORT' | 'NEUTRAL'} bias
 * @returns {string} RGBA color
 */
export const getBiasBg = (bias) => {
  const upperBias = String(bias).toUpperCase();
  if (upperBias === 'LONG' || upperBias === 'BULLISH' || upperBias === 'BUY') {
    return QAI_COLORS.semantic.successMuted;
  }
  if (upperBias === 'SHORT' || upperBias === 'BEARISH' || upperBias === 'SELL') {
    return QAI_COLORS.semantic.dangerMuted;
  }
  return 'rgba(136, 146, 176, 0.12)';
};

/**
 * Get score color based on value (0-100)
 * @param {number} score
 * @returns {string} Color hex
 */
export const getScoreColor = (score) => {
  if (score >= 70) return QAI_COLORS.semantic.success;
  if (score >= 40) return QAI_COLORS.semantic.warning;
  return QAI_COLORS.semantic.danger;
};

/**
 * Get score gradient for progress bars
 * @param {number} score (0-100)
 * @returns {string} CSS gradient
 */
export const getScoreGradient = (score) => {
  if (score >= 70) {
    return `linear-gradient(90deg, ${QAI_COLORS.semantic.success} 0%, #00CC6A 100%)`;
  }
  if (score >= 40) {
    return `linear-gradient(90deg, ${QAI_COLORS.semantic.warning} 0%, #FFCC00 100%)`;
  }
  return `linear-gradient(90deg, ${QAI_COLORS.semantic.danger} 0%, #CC3D4A 100%)`;
};

/**
 * Format terminology (replace cryptic abbreviations)
 * @param {string} term
 * @returns {string} Human-readable term
 */
export const formatTerminology = (term) => {
  const termMap = {
    'B60': 'Above EMA60',
    'B100': 'Above EMA100',
    'OVERE': 'Overextended',
    'ATRx': 'ATR %',
    'BOS': 'Break of Structure',
    'CHoCH': 'Change of Character',
    'OB': 'Order Block',
    'FVG': 'Fair Value Gap',
  };
  return termMap[term] || term;
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT STYLE PRESETS
// ═══════════════════════════════════════════════════════════════════════════

export const QAI_PRESETS = {
  // Card styling
  card: {
    backgroundColor: QAI_COLORS.background.surface,
    borderRadius: QAI_RADIUS.lg,
    border: `1px solid ${QAI_COLORS.border.default}`,
    boxShadow: QAI_SHADOWS.card,
    transition: QAI_TRANSITIONS.fast,
  },

  // Section header (DNA panels)
  sectionHeader: {
    ...QAI_TYPOGRAPHY.sectionHeader,
    color: QAI_COLORS.brand.primary, // CYAN instead of red!
    display: 'flex',
    alignItems: 'center',
    gap: QAI_SPACING.xs,
  },

  // Score badge
  scoreBadge: {
    ...QAI_TYPOGRAPHY.score,
    padding: `${QAI_SPACING.xxs} ${QAI_SPACING.xs}`,
    borderRadius: QAI_RADIUS.sm,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Verdict line
  verdict: {
    ...QAI_TYPOGRAPHY.body,
    color: QAI_COLORS.text.secondary,
    padding: `${QAI_SPACING.xs} ${QAI_SPACING.sm}`,
    backgroundColor: QAI_COLORS.background.input,
    borderRadius: QAI_RADIUS.sm,
    borderLeft: `3px solid ${QAI_COLORS.brand.primary}`,
  },

  // Tooltip
  tooltip: {
    ...QAI_TYPOGRAPHY.bodySmall,
    backgroundColor: QAI_COLORS.background.elevated,
    color: QAI_COLORS.text.primary,
    padding: `${QAI_SPACING.xs} ${QAI_SPACING.sm}`,
    borderRadius: QAI_RADIUS.sm,
    boxShadow: QAI_SHADOWS.dropdown,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default {
  colors: QAI_COLORS,
  fonts: QAI_FONTS,
  typography: QAI_TYPOGRAPHY,
  typeScale: QAI_TYPE_SCALE,      // PHASE 1.1: Type scale tokens
  tracking: QAI_TRACKING,         // PHASE 1.1: Letter spacing
  spacing: QAI_SPACING,
  heights: QAI_HEIGHTS,           // PHASE 1.2: Component heights
  widths: QAI_WIDTHS,             // PHASE 1.2: Layout widths
  radius: QAI_RADIUS,
  layout: QAI_LAYOUT,             // UI Density Enhancement
  shadows: QAI_SHADOWS,
  transitions: QAI_TRANSITIONS,
  icons: QAI_ICONS,
  presets: QAI_PRESETS,
  // Helpers
  getBiasColor,
  getBiasBg,
  getScoreColor,
  getScoreGradient,
  formatTerminology,
};
